import { api } from '../client/axiosClient';
import { CustomResponseEntity, PaginatedResponse } from '../types/common';
import { getApiUrl } from '../config/apiConfig';
import type {
  ActivityFeedItemDto,
  ActivityLogFilterParams,
  ActivityQueryParams,
  ChildOrgsOrderedParams,
  ChildOrgWithActivityDto,
  HealthSummaryDto,
} from '../types/dtos/activity-log/activity-log.dto';

/**
 * API service for the activity feed and health summary.
 *
 * Organization context is provided via the X-Organization-Id header
 * (injected automatically by axiosClient). No orgId in URL paths.
 */
export class ActivityLogService {
  getFeed(params: ActivityLogFilterParams): Promise<CustomResponseEntity<PaginatedResponse<ActivityFeedItemDto>>> {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.eventType) queryParams.append('eventType', params.eventType);
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.after) queryParams.append('after', params.after);
    queryParams.append('size', String(params.size ?? 20));

    return api.get(getApiUrl(`/activity-feed?${queryParams.toString()}`));
  }

  getChildOrgFeed(childOrgId: string, params: ActivityLogFilterParams): Promise<CustomResponseEntity<PaginatedResponse<ActivityFeedItemDto>>> {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.eventType) queryParams.append('eventType', params.eventType);
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.after) queryParams.append('after', params.after);
    queryParams.append('size', String(params.size ?? 20));

    return api.get(getApiUrl(`/activity-feed/org/${childOrgId}?${queryParams.toString()}`));
  }

  getHealthSummary(): Promise<CustomResponseEntity<HealthSummaryDto>> {
    return api.get(getApiUrl(`/health-summary`));
  }

  queryOrgs(params: ActivityQueryParams): Promise<CustomResponseEntity<PaginatedResponse<Record<string, unknown>>>> {
    const queryParams = new URLSearchParams();
    queryParams.append('query', params.query);
    queryParams.append('page', String(params.page ?? 0));
    queryParams.append('size', String(params.size ?? 20));

    return api.get(getApiUrl(`/health-summary/query?${queryParams.toString()}`));
  }

  getChildOrgsOrdered(params: ChildOrgsOrderedParams): Promise<CustomResponseEntity<PaginatedResponse<ChildOrgWithActivityDto>>> {
    const queryParams = new URLSearchParams();
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.entityType) queryParams.append('entityType', params.entityType);
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.activityFilter) queryParams.append('activityFilter', params.activityFilter);
    queryParams.append('page', String(params.page ?? 0));
    queryParams.append('size', String(params.size ?? 20));

    return api.get(getApiUrl(`/health-summary/orgs?${queryParams.toString()}`));
  }
}

export const activityLogService = new ActivityLogService();
